import React from 'react';
import PropTypes from 'prop-types';

import {useSelector} from 'react-redux';
import OutputText from '@ey/canvascoreservices/OutputText';
import {IconoirTrash, IconoirEditPencil} from '@ey-xd/motif-icon';

import {StyledColumns, StyledBody} from './StyledCustomizeTableModal';

import {labels} from '../../../../util/utils';
import {projectSessionKeys, StandardColumns} from '../../../../util/uiconstants';
import utilityfunctions from '../../../../util/utilityfunctions';
import Utility from '../../../../util/utilityfunctions';

import Form from '@ey/canvascoreservices/Form';
import Hr from '../../../Common/Hr/HrAlt';
import LoadingWrapper from '../../../CoreWrappers/LoadingWrapper';
import {loadStorage, saveStorage} from '../../../Common/localStorage';
import selectAccessPermissions from '../../../../reducers/Project/selectors/selectAccessPermissions';
import Button from '../../../CoreWrappers/Button';
import Checkbox from '../../../CoreWrappers/Checkbox';

const SelectColumnsForm = ({isLoading, userColumns, setUserColumns, projectId, changeMode}) => {
	//To check if user is owner
	const userGui = useSelector((state) => state.userPreferences?.gui);
	const userListPerm = useSelector((state) => state.project?.users);
	const userData = userListPerm?.find(({userId}) => userId === userGui);
	const isProjectOwner = !!userData?.isProjectOwner;
	const COLUMNS = 'title';
	const projectSessionKey = `${projectSessionKeys.userColumns}_${projectId}`;
	const selectedColumns = loadStorage(projectSessionKey);
	const {groupRiskAssessmentAccess} = selectAccessPermissions();
	//#region render small components
	const renderHeader = (text) => <OutputText>{text}</OutputText>;

	//#endregion render small components

	const initialValues = {
		[COLUMNS]: selectedColumns || saveStorage(projectSessionKey, StandardColumns.columns)
	};

	const schema = {
		[COLUMNS]: {required: false}
	};
	const props = {
		initialValues,
		schema
	};
	return (
		<Form {...props}>
			{() => {
				const handlerOnColumnSelectionChange = (e, columnId) => {
					let newSelection = [...userColumns];
					const viewIndex = newSelection.findIndex((x) => x.id === columnId);
					newSelection[viewIndex].isChecked = !newSelection[viewIndex].isChecked;
					setUserColumns(newSelection);
				};

				const renderBody = () => {
					const renderCheckBox = ({id, labels: labelId, isChecked}) => {
						const label = labels[labelId] || labelId;
						const props = {
							id,
							key: id,
							className: 'checkBox',
							styleName: 'checkoption-item',
							elementStyleName: 'ellipsis-one-line',
							ellipse: true,
							noOfLines: 1,
							label: renderCheckboxLabel(label),
							title: label,
							checked: isChecked,
							onChange: (e) => handlerOnColumnSelectionChange(e, id)
						};
						return <Checkbox {...props} />;
					};

					const renderCheckboxLabel = (label) => {
						return (
							<OutputText ellipsis={true} title={label} useTypography={false}>
								{label}
							</OutputText>
						);
					};

					const renderDeleteIcon = (x) => {
						const handlerOnClick = () => {
							let uColumns = userColumns.filter((c) => c.id != x.id);
							setUserColumns([...uColumns]);
						};

						const props = {
							title: labels.delete,
							svg: IconoirTrash,
							variant: 'ghost',
							onClick: () => handlerOnClick(),
							iconSize: 20
						};
						return <Button {...props} />;
					};

					const renderEditIcon = (x) => {
						const handlerOnClick = () => {
							changeMode(x);
						};

						const props = {
							title: labels.edit,
							svg: IconoirEditPencil,
							variant: 'ghost',
							onClick: () => handlerOnClick(),
							iconSize: 20
						};
						return <Button {...props} />;
					};

					const renderDeleteTextLegend = () => (
						<OutputText body2 light>
							{Utility.createRichTextElement(labels.deleteCustomColumnLegend)}
						</OutputText>
					);

					const renderColumns = (title, columns = [], isStandard = true) => (
						<>
							{renderHeader(title)}
							{!isStandard && isProjectOwner && columns?.length > 0 && renderDeleteTextLegend()}
							<StyledColumns isProjectOwner={!isProjectOwner} isStandard={isStandard}>
								{columns?.length === 0 ? (
									<OutputText body2 light>
										{labels.noCustomColumnMsg}
									</OutputText>
								) : (
									columns.map((x) => {
										return (
											<div className="columns">
												{renderCheckBox(x)}
												{!isStandard && isProjectOwner && renderEditIcon(x)}
												{!isStandard && isProjectOwner && renderDeleteIcon(x)}
											</div>
										);
									})
								)}
							</StyledColumns>
						</>
					);

					const getColumnsBasedOnAccessAndProjectType = () => {
						return utilityfunctions.checkProjectTypeAndFilterColumns(
							userColumns?.filter((col) => !col.isCustom),
							groupRiskAssessmentAccess
						);
					};

					return (
						<StyledBody>
							{renderColumns(labels.standardColumns, getColumnsBasedOnAccessAndProjectType())}
							<Hr />
							{renderColumns(
								labels.customColumns,
								utilityfunctions.sortListData(userColumns?.filter((x) => x.isCustom) ?? [], 'label'),
								false
							)}
						</StyledBody>
					);
				};

				return (
					<LoadingWrapper isLoading={isLoading}>
						<OutputText body1 light>
							{Utility.createRichTextElement(labels.InstructionalTextCustomizeTableModal)}
						</OutputText>
						{renderBody()}
					</LoadingWrapper>
				);
			}}
		</Form>
	);
};

SelectColumnsForm.defaultProps = {
	isLoading: false,
	userColumns: [],
	setUserColumns: null,
	projectId: ''
};

SelectColumnsForm.propTypes = {
	isLoading: PropTypes.bool,
	// eslint-disable-next-line react/forbid-prop-types
	userColumns: PropTypes.arrayOf(PropTypes.any),
	setUserColumns: PropTypes.func,
	projectId: PropTypes.string
};

export default SelectColumnsForm;
